// React & Third-party Libraries
import { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";

// Common Components
import { When } from "@/components/common/When";
import IFThen from "@/components/common/IFThen";

// Page-specific Components
import RenderTypes from "../Types/RenderTypes";
import PreviewResult from "../PreviewResult/Preview/PreviewResult";
import QrCustomization from "../QrCustomization/QrCustomization";
import { TypesSelectbox } from "./TypesSelectbox";
import Download from "@/components/page-modules/home-page/GenerateTypes/PreviewResult/Download/Download";
import PreviewRegister from "@/components/page-modules/home-page/GenerateTypes/PreviewResult/Register/PreviewRegister";
import SignupModalContainer from "@/components/page-modules/home-page/GenerateTypes/PreviewResult/SignupModal/SignupModalContainer";

// Constants
import { mobileHomeTabs, VARIANT } from "@/constant";

// State Management (Zustand, Context, etc.)
import { useCustomLogo, useUserDesign } from "@/states/design.state";
import { useRegisterTrack } from "@/states/user.state";
import { useRegisterRequired, useSignUpModal } from "@/states/ui.state";
import { useSolutionsSelect } from "@/states/solution.state";

// Styles
import style from "./TypesPreview.module.scss";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
const SignupPopupMobile = dynamic(() => import("@/components/common/SignUpPopup/SignUpPopupMobile/SignUpPopupMobile"));
const TypesButton = dynamic(() => import("../TypesButton/TypesButton"), {
  loading: () => <Skeleton height={62} />,
  ssr: false
});
const TypesPreview = ({
  TEST_VARIANT = VARIANT.VariantB
}: {
  TEST_VARIANT?: VARIANT;
}) => {
  const t = useTranslations("Home.mobile");
  const {
    setShape
  } = useUserDesign();
  const {
    isSignUpModal,
    setIsSignUpModal
  } = useSignUpModal();
  const {
    solutionsSelectedTypes
  } = useSolutionsSelect();
  const {
    setCustomLogo
  } = useCustomLogo.getState();
  const {
    setIsTypeRequireRegister
  } = useRegisterRequired();
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [registerInPreview, setRegisterInPreview] = useRegisterTrack(state => [state.registerInPreview, state.setRegisterInPreview]);
  useEffect(() => {
    setActiveTab(isDesktop ? 2 : 1);
    setIsTypeRequireRegister(false);
  }, [isDesktop]);
  useEffect(() => {
    if (isDesktop) {
      setCustomLogo({
        logo: null,
        name: ""
      });
    }
    return () => {
      if (isDesktop) {
        setCustomLogo({
          logo: null,
          name: ""
        });
        setShape({
          body_type: "default",
          edges: "default"
        });
      }
    };
  }, []);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1025);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    setIsMobile(window.innerWidth <= 500);
    const handleResize = () => setIsMobile(window.innerWidth <= 500);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const onCloseModal = useCallback(() => {
    setIsSignUpModal(false);
  }, []);
  const handleActiveTab = (param: number) => {
    setActiveTab(isDesktop ? 2 : param);
  };
  const renderMobileTabs = () => <div className={style.steps} data-sentry-component="renderMobileTabs" data-sentry-source-file="TypesPreview.tsx">
      {mobileHomeTabs.map(item => <button key={item.id} className={cn({
      [style.active]: activeTab === item.id
    })} onClick={() => {
      handleActiveTab(item.id);
    }}>
          {(solutionsSelectedTypes === "url" || !solutionsSelectedTypes) && item.id === 1 ? t(item.titleForWebType) : t(item.title)}
        </button>)}
    </div>;
  const renderContent = () => {
    if (isDesktop) {
      return <QrCustomization />;
    }
    if (activeTab === 1) {
      return <div className={style.selectTypeBox}>
          <p className={style.title}>{t("chooseQRCodeType")}</p>
          <TypesSelectbox />
          <div className={style.renderTypeBox}>
            <RenderTypes />
          </div>
        </div>;
    }
    return <QrCustomization data-sentry-element="QrCustomization" data-sentry-component="renderContent" data-sentry-source-file="TypesPreview.tsx" />;
  };
  return <SkeletonTheme baseColor="#ffffff" highlightColor="#e6f4f4" borderRadius={10} data-sentry-element="SkeletonTheme" data-sentry-component="TypesPreview" data-sentry-source-file="TypesPreview.tsx">
      <div id="builder_wrapper" className={style.builderWrapper}>
        <div className={style.container}>
          <When condition={isDesktop} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">
            <TypesButton data-sentry-element="TypesButton" data-sentry-source-file="TypesPreview.tsx" />
          </When>
          <div className={style.flex}>
            <div className={cn({
            [style.mainMobile_container]: !isDesktop,
            [style.mainDesktop_container]: isDesktop,
            [style.renderTypes_container]: !isDesktop
          })}>
              <When condition={isDesktop} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">
                <div className={style.renderTypes_container}>
                  <RenderTypes data-sentry-element="RenderTypes" data-sentry-source-file="TypesPreview.tsx" />
                </div>
              </When>

              <div className={cn({
              [style.mobileDesign_tabs]: !isDesktop,
              [style.desktopDesign_tabs]: isDesktop
            })}>
                <When condition={!isDesktop} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">{renderMobileTabs()}</When>
                <div className={cn(style.tabsBoxContainer)}>{renderContent()}</div>
              </div>
            </div>

            <div className={cn({
            [style.previewResult_container]: isDesktop,
            [style.mainMobile_container]: !isDesktop
          })}>
              <PreviewResult TEST_VARIANT={TEST_VARIANT} popupInPreview={registerInPreview} data-sentry-element="PreviewResult" data-sentry-source-file="TypesPreview.tsx">
                <When condition={registerInPreview} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">
                  <PreviewRegister closeModalInPreview={() => setRegisterInPreview(false)} data-sentry-element="PreviewRegister" data-sentry-source-file="TypesPreview.tsx" />
                </When>
                <When condition={!registerInPreview} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">
                  <Download setActiveTab={() => setActiveTab(1)} TEST_VARIANT={TEST_VARIANT} handlePopupInPreview={() => setRegisterInPreview(true)} data-sentry-element="Download" data-sentry-source-file="TypesPreview.tsx" />
                </When>
              </PreviewResult>
              <When condition={!isDesktop} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">
                <Download TEST_VARIANT={TEST_VARIANT} setActiveTab={() => setActiveTab(1)} data-sentry-element="Download" data-sentry-source-file="TypesPreview.tsx" />
              </When>
            </div>
          </div>
        </div>
        <When condition={isSignUpModal} data-sentry-element="When" data-sentry-source-file="TypesPreview.tsx">
          <IFThen condition={isMobile} Then={<SignupPopupMobile />} Else={<SignupModalContainer TEST_VARIANT={TEST_VARIANT} isOPen={isSignUpModal} handleClose={onCloseModal} />} data-sentry-element="IFThen" data-sentry-source-file="TypesPreview.tsx" />
        </When>
      </div>
    </SkeletonTheme>;
};
export default TypesPreview;